import { useEffect, useState } from 'react';
import { Modal, Form, Button, Row, Col, Input, DatePicker, Select, notification } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useLocation, matchPath } from 'react-router-dom';

import { useGetBookingDetail } from 'hooks/useGetBookingDetail';
import useModalStore from 'stores/useModal';
import useBookingStore from 'stores/useBooking';
import PAGES from 'routes/constants';

import { ReactComponent as IconClose } from 'assets/images/close.svg';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ParamsCreateBookingRemind } from 'services/api/type/booking.type';
import API from 'services/api/index';
import dayjs from 'dayjs';
import { REMIND_CATEGORIES_OPTIONS, RemindCategoryType } from './remind-categories-option';
import QUERY_KEYS from 'services/api/queryKeys';
import { queryClient } from 'index';

interface BookingRemindModalProps {
  onSuccess?: () => void;
}

type ModalStep = 'search' | 'create';

function BookingRemindModal({ onSuccess }: BookingRemindModalProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [form] = Form.useForm<ParamsCreateBookingRemind>();
  const [searchForm] = Form.useForm<{ booking_line_id: number }>();

  const { bookingLineId: storeBookingLineId, setBookingLineId } = useBookingStore();
  const { isOpenBookingRemind, setIsOpenBookingRemind } = useModalStore();

  const [step, setStep] = useState<ModalStep>('search');
  const [selectedBookingLineId, setSelectedBookingLineId] = useState<number | undefined>(undefined);

  const bookingLineId = storeBookingLineId || selectedBookingLineId;

  const { data: bookingLineDetail, isLoading } = useGetBookingDetail(bookingLineId);

  const { mutateAsync: mutateCreateBookingRemind, isPending } = useMutation({
    mutationFn: (params: ParamsCreateBookingRemind) => API.booking.createBookingRemind(params)
  });

  useEffect(() => {
    if (bookingLineDetail && step === 'create') {
      form.setFieldsValue({
        booking_line_id: bookingLineDetail.booking_line_id
      });
    }
  }, [bookingLineDetail, step, form]);

  useEffect(() => {
    if (!isOpenBookingRemind && matchPath(PAGES.receptionistBookingReminds, location.pathname)) {
      setBookingLineId(0);
    }
    // If opened from context menu with storeBookingLineId, go directly to create step
    if (isOpenBookingRemind && storeBookingLineId) {
      setStep('create');
      setSelectedBookingLineId(undefined);
    } else if (isOpenBookingRemind) {
      // If opened from page (no storeBookingLineId), start with search step
      setStep('search');
    }
  }, [isOpenBookingRemind, setBookingLineId, storeBookingLineId, location.pathname]);

  const handleSearch = async () => {
    try {
      const values = await searchForm.validateFields();
      if (values.booking_line_id) {
        setSelectedBookingLineId(Number(values.booking_line_id));
      }
    } catch (error) {
      // Validation failed
      notification.error({
        message: t('common.error')
      });
    }
  };

  const handleCloseModal = () => {
    setIsOpenBookingRemind(false);
    setSelectedBookingLineId(undefined);
    setStep('search');
    form.resetFields();
    searchForm.resetFields();
  };

  const handleSelectBookingLine = () => {
    if (bookingLineDetail) {
      form.setFieldsValue({
        booking_line_id: bookingLineDetail.booking_line_id
      });
      setStep('create');
    }
  };

  const handleBackToSearch = () => {
    setStep('search');
    setSelectedBookingLineId(undefined);
    form.resetFields();
  };

  const handleFinish = async (values: ParamsCreateBookingRemind) => {
    console.log('values', values);
    try {
      await mutateCreateBookingRemind({
        ...values,
        date: dayjs(values.date).format('YYYY-MM-DD')
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GET_BOOKING_REMINDS] });
      handleCloseModal();
      notification.success({
        message: t('common.message.bookingRemindCreatedSuccessfully')
      });
      onSuccess?.();
    } catch (error: any) {
      console.log('error', error);
      notification.error({
        message: error.error || t('common.error')
      });
    }
  };

  // Render search step
  const renderSearchStep = () => (
    <Form form={searchForm} layout="vertical">
      <Form.Item
        label={t('common.bookingRemind.bookingLineId')}
        name="booking_line_id"
        rules={[
          {
            required: true,
            message: t('common.bookingRemind.bookingLineIdRequired')
          }
        ]}
      >
        <Input
          placeholder={t('common.bookingRemind.enterBookingLineId')}
          suffix={
            <SearchOutlined
              style={{ cursor: 'pointer', color: '#1890ff' }}
              onClick={handleSearch}
            />
          }
          onPressEnter={handleSearch}
        />
      </Form.Item>

      {selectedBookingLineId && (
        <div
          style={{
            padding: '16px',
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            marginTop: '16px'
          }}
        >
          {isLoading ? (
            <p>{t('common.actions.loading')}</p>
          ) : bookingLineDetail ? (
            <>
              <Row gutter={16}>
                <Col span={8}>
                  <p style={{ margin: 0 }}>
                    <strong>{t('common.bookingRemind.bookingId')}:</strong>{' '}
                    {bookingLineDetail.booking_id}
                  </p>
                </Col>
                <Col span={8}>
                  <p style={{ margin: 0 }}>
                    <strong>{t('common.bookingRemind.bookingLineId')}:</strong>{' '}
                    {bookingLineDetail.booking_line_id}
                  </p>
                </Col>
                <Col span={8}>
                  <p style={{ margin: 0 }}>
                    <strong>{t('common.bookingRemind.roomNo')}:</strong>{' '}
                    {bookingLineDetail.room_name || 'N/A'}
                  </p>
                </Col>
              </Row>
            </>
          ) : (
            <p style={{ margin: 0, color: '#ff4d4f' }}>
              {t('common.bookingRemind.bookingLineNotFound')}
            </p>
          )}
        </div>
      )}
    </Form>
  );

  // Render create step
  const renderCreateStep = () => (
    <Form form={form} layout="vertical" onFinish={handleFinish} preserve={true}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item label={t('common.bookingRemind.bookingLineId')} name="booking_line_id">
            <Input disabled />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label={t('common.bookingRemind.roomNo')}>
            <Input value={bookingLineDetail?.room_name || ''} disabled />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label={t('common.bookingRemind.remindDate')}
        name="date"
        rules={[
          {
            required: true,
            message: t('common.bookingRemind.remindDateRequired')
          }
        ]}
      >
        <DatePicker
          style={{ width: '100%' }}
          format="DD/MM/YYYY"
          placeholder={t('common.time.selectDate')}
        />
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) => prevValues.category !== currentValues.category}
      >
        {({ getFieldValue }) => {
          const isOther = getFieldValue('category') === RemindCategoryType.OTHER;
          return (
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label={t('common.bookingRemind.remindCategory')}
                  name="category"
                  rules={[
                    {
                      required: true,
                      message: t('common.bookingRemind.remindCategoryRequired')
                    }
                  ]}
                >
                  <Select
                    placeholder={t('common.bookingRemind.selectCategory')}
                    options={REMIND_CATEGORIES_OPTIONS.map(option => ({
                      ...option,
                      label: t(option.label)
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={16}>
                <Form.Item
                  label={t('common.bookingRemind.customCategory')}
                  name="category_display"
                  rules={[
                    {
                      required: isOther,
                      message: t('common.bookingRemind.customCategoryRequired')
                    }
                  ]}
                >
                  <Input
                    placeholder={t('common.bookingRemind.customCategoryPlaceholder')}
                    disabled={!isOther}
                  />
                </Form.Item>
              </Col>
            </Row>
          );
        }}
      </Form.Item>

      <Form.Item
        label={t('common.bookingRemind.remindContent')}
        name="note"
        rules={[
          {
            required: true,
            message: t('common.bookingRemind.remindContentRequired')
          }
        ]}
      >
        <Input.TextArea rows={4} placeholder={t('common.bookingRemind.remindContentPlaceholder')} />
      </Form.Item>
    </Form>
  );

  // Render footer based on step
  const renderFooter = () => {
    if (step === 'search') {
      return [
        <Button key="cancel" onClick={handleCloseModal}>
          {t('common.actions.cancel')}
        </Button>,
        <Button
          key="select"
          className="ant-btn-secondary btn-submit"
          disabled={!bookingLineDetail || isLoading}
          onClick={handleSelectBookingLine}
        >
          {t('common.actions.select')}
        </Button>
      ];
    }

    return [
      !storeBookingLineId && (
        <Button key="back" onClick={handleBackToSearch}>
          {t('common.actions.back')}
        </Button>
      ),
      <Button
        key="submit"
        className="ant-btn-secondary btn-submit"
        loading={isPending}
        onClick={() => {
          form
            .validateFields()
            .then(values => {
              handleFinish(values);
            })
            .catch(error => {
              console.log('Validation failed:', error);
            });
        }}
      >
        {t('common.actions.save')}
      </Button>
    ].filter(Boolean);
  };

  return (
    <Modal
      title={
        step === 'search'
          ? t('common.bookingRemind.searchBookingLine')
          : t('common.bookingRemind.title')
      }
      centered
      width={600}
      open={isOpenBookingRemind}
      onCancel={handleCloseModal}
      closeIcon={<IconClose />}
      footer={renderFooter()}
    >
      <div style={{ display: step === 'search' ? 'block' : 'none' }}>{renderSearchStep()}</div>
      <div style={{ display: step === 'create' ? 'block' : 'none' }}>{renderCreateStep()}</div>
    </Modal>
  );
}

export default BookingRemindModal;
